import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { catchError, exhaustMap, filter, map, of, switchMap, tap } from 'rxjs';
import { AuthActions } from '../../auth/state';
import { DEFAULT_PROFILE_IMAGE_URL } from '../../../core/constants/profile-image.constants';
import { ToastService } from '../../../core/services/toast.service';
import { StudentPortalService } from '../../../core/services/student-portal.service';
import { SettingsActions } from './settings.actions';
import { readStoredToken } from '../../../core/utils/auth-session-storage';

export class SettingsEffects {
  private readonly actions$ = inject(Actions);
  private readonly studentPortalService = inject(StudentPortalService);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);

  readonly loadProfileImageOnSession$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loginSuccess, AuthActions.init),
      map((action) =>
        action.type === AuthActions.loginSuccess.type ? action.token : readStoredToken(),
      ),
      filter((token): token is string => Boolean(token)),
      map(() => SettingsActions.loadProfileImage()),
    ),
  );

  readonly clearProfileImageOnSessionEnd$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.logout, AuthActions.sessionExpired, AuthActions.sessionEndedElsewhere),
      map(() => SettingsActions.setProfileImageUrl({ url: '' })),
    ),
  );

  readonly loadProfileImage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SettingsActions.loadProfileImage),
      switchMap(() =>
        this.studentPortalService.getCurrentStudentProfile().pipe(
          map((profile) => SettingsActions.setProfileImageUrl({ url: profile.profileImageUrl })),
          catchError(() =>
            of(SettingsActions.setProfileImageUrl({ url: DEFAULT_PROFILE_IMAGE_URL })),
          ),
        ),
      ),
    ),
  );

  readonly uploadProfileImage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SettingsActions.uploadProfileImage),
      exhaustMap(({ file }) =>
        this.studentPortalService.uploadProfileImage(file).pipe(
          tap(() =>
            this.toastService.success(
              this.translateService.instant('settings.profileImage.uploadSuccess'),
            ),
          ),
          map(({ url }) => SettingsActions.uploadProfileImageSuccess({ url })),
          catchError((error) => {
            const message =
              typeof error?.error?.message === 'string'
                ? error.error.message
                : this.translateService.instant('settings.profileImage.uploadError');
            this.toastService.error(message);

            return of(SettingsActions.uploadProfileImageFailure({ error: message }));
          }),
        ),
      ),
    ),
  );
}
